import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronRight,
  Heart,
  Calendar,
  MessageCircle,
  Gift,
  Star,
  ArrowRight,
  Menu,
  X,
  Flame,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Sparkles,
  ChevronDown,
  HelpCircle,
  Users,
  Award,
  Lock,
  Clock,
  ThumbsUp,
  SmilePlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { trackPageView, trackDownloadClick } from '../lib/track';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// App Store Links
const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';
const LOGO_URL = '/couplein_logo.png';

// Real app screenshots (provided by user)
const SHOTS = {
  mood:     '/shots/mood_sync.png',
  brownies: '/shots/brownie_points.png',
  calendar: '/shots/calendar_reminders.png',
  conflict: '/conflict_real_faces.png',
  streak:   '/shots/streak_celebration.png',
};

// Phone Mockup Component (responsive; compact variant for gallery grids)
const PhoneMockup = ({ children, className = '', compact = false }) => (
  <div className={`relative ${className}`}>
    {/* Phone frame */}
    <div className={`relative mx-auto bg-gray-900 rounded-[2rem] sm:rounded-[3rem] p-2 sm:p-3 shadow-2xl ${
      compact
        ? 'w-[150px] h-[315px] sm:w-[220px] sm:h-[460px] lg:w-[260px] lg:h-[540px]'
        : 'w-[220px] h-[460px] sm:w-[250px] sm:h-[520px] lg:w-[280px] lg:h-[580px]'
    }`}>
      {/* Notch */}
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 bg-gray-900 rounded-b-2xl z-20 ${compact ? 'w-16 sm:w-24 h-4 sm:h-6' : 'w-24 sm:w-32 h-6 sm:h-7'}`} />
      {/* Screen */}
      <div className="w-full h-full bg-white rounded-[1.5rem] sm:rounded-[2.5rem] overflow-hidden">
        {children}
      </div>
    </div>
  </div>
);

// App Screenshot, uses real product screenshots provided by owner
const AppScreenshot = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    className="block w-full h-full object-cover object-top"
  />
);

// Features data, "Because every couple..." framing, Brownie Points leads
const features = [
  {
    icon: Gift,
    title: 'Brownie',
    subtitle: 'Points',
    feeling: 'Because every couple says "thanks" a thousand times, and forgets it a thousand more.',
    description: 'Turn the small daily kindness into points you exchange with each other. The fun engine of your relationship.',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
  },
  {
    icon: Calendar,
    title: 'Shared',
    subtitle: 'Calendar',
    feeling: 'Because every couple has had "you-didn\'t-tell-me" Sundays.',
    description: 'Two calendars, one source of truth. Birthdays, dates, dentist appointments. Both of you, always on the same page.',
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-50',
  },
  {
    icon: Heart,
    title: '60-Second',
    subtitle: 'Check-in',
    feeling: "Because every couple has bad days they don't talk about.",
    description: 'A daily mood ping that takes a minute and saves you a hundred misunderstandings.',
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-50',
  },
  {
    icon: MessageCircle,
    title: 'Guided',
    subtitle: 'Mode',
    feeling: 'Because every couple disagrees, and most of us are terrible at it.',
    description: 'A 5-minute fair-turn conversation for when you need to actually be heard. No therapist required.',
    color: 'from-blue-500 to-purple-500',
    bgColor: 'bg-blue-50',
  },
];

// Testimonials
const testimonials = [
  {
    name: 'Priya & Tom',
    years: '4 years together',
    text: "CoupleIn made everyday love feel like a game we both win. We earn points for the silliest stuff: coffee runs, foot rubs, surprise emojis.",
    avatar: '💑',
  },
  {
    name: 'Emma & James',
    years: '5 years married',
    text: "Honestly, since CoupleIn we just stopped forgetting things. Anniversaries, dentist appointments, the trash. It sounds small until you live it.",
    avatar: '👫',
  },
  {
    name: 'Alex & Jordan',
    years: 'Engaged',
    text: "We hit a rough patch last winter and CoupleIn's Guided Mode helped us actually hear each other. Now we use it once a month, like a relationship oil change.",
    avatar: '💕',
  },
];

// Download Buttons Component
const DownloadButtons = ({ size = 'default', className = '' }) => (
  <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
    <a
      href={IOS_LINK}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackDownloadClick('home')}
      className={`flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 ${size === 'large' ? 'px-6 py-3.5 sm:py-4' : 'px-4 py-3'}`}
    >
      <svg className={size === 'large' ? 'w-8 h-8' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Download on the</p>
        <p className={`font-semibold ${size === 'large' ? 'text-lg' : 'text-sm'} -mt-1`}>App Store</p>
      </div>
    </a>
    <a
      href={ANDROID_LINK}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackDownloadClick('home')}
      className={`flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 ${size === 'large' ? 'px-6 py-3.5 sm:py-4' : 'px-4 py-3'}`}
    >
      <svg className={size === 'large' ? 'w-8 h-8' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor">
        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Get it on</p>
        <p className={`font-semibold ${size === 'large' ? 'text-lg' : 'text-sm'} -mt-1`}>Google Play</p>
      </div>
    </a>
  </div>
);

// Sticky mobile install bar, appears after user scrolls past hero
const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const root = document.getElementById('root');
    const target = root || window;
    const getScrollY = () => (root ? root.scrollTop : window.scrollY);
    const onScroll = () => setVisible(getScrollY() > 600);
    target.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => target.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div
      className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-gray-200 px-3 py-2.5 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] transition-transform duration-300 ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
      data-testid="sticky-mobile-cta"
    >
      <div className="flex items-center gap-2">
        <img src={LOGO_URL} alt="" className="h-8 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-900 truncate">Get CoupleIn free</p>
          <p className="text-[10px] text-gray-500 truncate">60-sec setup · No card</p>
        </div>
        <a
          href={IOS_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 bg-black text-white rounded-lg px-3 py-2 text-xs font-semibold active:scale-95 transition-transform"
          data-testid="sticky-ios-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          iOS
        </a>
        <a
          href={ANDROID_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 bg-black text-white rounded-lg px-3 py-2 text-xs font-semibold active:scale-95 transition-transform"
          data-testid="sticky-android-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
          </svg>
          Android
        </a>
      </div>
    </div>
  );
};

import Navbar from '../components/Navbar';

const Landing = () => {
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [latestPosts, setLatestPosts] = useState([]);
  const screenshots = [
    { component: <AppScreenshot src={SHOTS.brownies} alt="CoupleIn Brownie Points, exchange kindness daily" />, label: 'Brownie Points', to: '/love-with-intentionality' },
    { component: <AppScreenshot src={SHOTS.calendar} alt="CoupleIn shared calendar, never forget a date" />, label: 'Shared Calendar', to: '/never-forget-anything' },
    { component: <AppScreenshot src={SHOTS.mood} alt="CoupleIn Mood, sync moods every day" />, label: '60-Second Check-in', to: '/stay-connected-in-60-seconds' },
    { component: <AppScreenshot src={SHOTS.conflict} alt="CoupleIn Guided Mode, resolve fights faster" />, label: 'Guided Mode', to: '/solve-fights-in-5-minutes' },
  ];
  
  // Scroll inside #root (global CSS already enables it). No body/html overrides.
  
  useEffect(() => { trackPageView('home'); }, []);

  // Fetch published blog articles if available (limit to latest 3 articles for performance)
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog/posts?limit=3`, { withCredentials: false });
        if (res.data?.posts && Array.isArray(res.data.posts) && res.data.posts.length > 0) {
          setLatestPosts(res.data.posts.slice(0, 3));
        }
      } catch (err) {
        // Quiet fallback if offline or no posts created yet
      }
    };
    fetchPosts();
  }, []);
  
  // Auto-rotate screenshots
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveScreenshot((prev) => (prev + 1) % screenshots.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [screenshots.length]);
  
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="CoupleIn - Best Couples' App for Relationship & Connection"
        description="CoupleIn helps couples celebrate the small stuff, remember the important moments, resolve fights calmly, and stay connected daily. Free on iOS & Android."
        path="/"
        keywords="couple app, relationship app, brownie points, shared calendar for couples, mood check-in, couples communication, couples app free, relationship quiz, love language test, conflict resolution for couples"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'MobileApplication',
              '@id': 'https://coupleinapp.com/#app',
              name: 'CoupleIn',
              description: 'CoupleIn helps couples celebrate the small stuff, remember the important stuff, and stay close on the busy stuff. Made for real couples doing their best.',
              applicationCategory: 'LifestyleApplication',
              operatingSystem: 'iOS, Android',
              installUrl: 'https://apps.apple.com/us/app/couplein/id6749887412',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
              aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', ratingCount: '1250', bestRating: '5', worstRating: '1' },
              author: { '@type': 'Organization', name: 'CoupleIn', url: 'https://coupleinapp.com' }
            },
            {
              '@type': 'FAQPage',
              '@id': 'https://coupleinapp.com/#faq',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: "Can I use CoupleIn if my partner hasn't downloaded it yet?",
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: "Yes, absolutely! You can start using CoupleIn right away to keep track of shared reminders, take quizzes, and log sweet gestures. When your partner is ready, simply share your 6-digit sync code or link and you will be paired in one tap."
                  }
                },
                {
                  '@type': 'Question',
                  name: "Is CoupleIn free on iOS and Android?",
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: "Yes! CoupleIn is free on the Apple App Store and Google Play Store. You get instant access to Brownie Points, Shared Calendar, 60-Second Mood Sync, and Guided Conflict Resolution without requiring a credit card."
                  }
                },
                {
                  '@type': 'Question',
                  name: "How does the Brownie Points system work?",
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: "You pick or create gestures that mean the most to you (like making coffee, giving a foot rub, or cooking dinner). Whenever your partner does it, you tap to give them points, which they can redeem for fun, customized rewards you both agreed on."
                  }
                },
                {
                  '@type': 'Question',
                  name: "How does 5-Minute Guided Mode resolve disagreements?",
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: "Instead of texting in anger or talking over each other, Guided Mode provides structured, timed turns where each partner shares how they feel without interruption, followed by actionable resolution prompts to help you reconnect."
                  }
                },
                {
                  '@type': 'Question',
                  name: "Is our data private and secure?",
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: "Yes. Your shared calendar, mood check-ins, gestures, and conversations are encrypted and accessible only by you and your paired partner. We never sell your personal relationship data."
                  }
                }
              ]
            }
          ]
        }}
      />
      <Navbar />
      <StickyMobileCTA />
      
      {/* Hero Section */}
      <section className="pt-24 pb-10 sm:pt-28 sm:pb-16 lg:pt-36 lg:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 bg-pink-50 text-pink-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                Made for real couples doing their best
              </div>
              
              <h1 className="text-[2.25rem] leading-[1.05] sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 sm:mb-6">
                CoupleIn
                <span className="block bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">
                  Stay Organized and Solve Issues Fast
                </span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                The <span className="font-semibold text-gray-900">first app in the world</span> that rewards you for loving better, helps you remember, and stops you from arguing.
              </p>
              
              <DownloadButtons size="large" className="justify-center lg:justify-start" />
              
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 italic text-center lg:text-left">
                Built by a couple who almost forgot their anniversary. Twice.
              </p>
              
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 mt-5 sm:mt-6 text-xs sm:text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span>4.9 Rating</span>
                </div>
                <div>10K+ Downloads</div>
                <div>Free · 60-sec setup</div>
              </div>
            </motion.div>
            
            {/* Right - Phone Mockups */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative flex justify-center">
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-pink-200 to-orange-200 blur-3xl opacity-30 rounded-full" />
                
                {/* Main phone */}
                <PhoneMockup className="relative z-10">
                  {screenshots[activeScreenshot].component}
                </PhoneMockup>
              </div>
              
              {/* Screenshot selector */}
              <div className="flex justify-center gap-3 mt-6">
                {screenshots.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveScreenshot(i)}
                    aria-label={`View ${screenshots[i]?.label || 'screenshot'} slide ${i + 1}`}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === activeScreenshot ? 'w-8 bg-pink-500' : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section id="features" className="py-12 sm:py-16 lg:py-24 bg-gray-50 scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
              Give us 7 days.
              <span className="block bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">
                If you are not happier, Uninstall!
              </span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
              Cheaper than therapy. Easier than nagging.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className={`w-14 h-14 ${feature.bgColor} rounded-2xl flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-7 h-7 bg-gradient-to-r ${feature.color} bg-clip-text text-pink-500`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  {feature.title}
                  <span className="block bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">
                    {feature.subtitle}
                  </span>
                </h3>
                <p className="text-sm font-medium text-pink-500 mt-2 mb-3">{feature.feeling}</p>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Screenshots Gallery */}
      <section id="screenshots" className="py-12 sm:py-16 lg:py-24 scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">See It In Action</h2>
            <p className="text-base sm:text-lg text-gray-600">Beautiful, intuitive design that just works</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 justify-items-center">
            {screenshots.map((screenshot, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <Link to={screenshot.to} data-testid={`gallery-link-${i}`} className="block group">
                  <PhoneMockup compact className="transform group-hover:scale-105 transition-transform duration-300">
                    {screenshot.component}
                  </PhoneMockup>
                  <p className="mt-3 sm:mt-4 text-sm sm:text-base font-semibold text-gray-700 group-hover:text-pink-500 transition-colors">{screenshot.label}</p>
                  <p className="text-xs text-pink-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Learn more →</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Milestone moment, streak as social proof */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-pink-50 via-white to-orange-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left, phone */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-center order-2 lg:order-1"
            >
              <PhoneMockup>
                <AppScreenshot src={SHOTS.streak} alt="CoupleIn 30-day streak celebration screen" />
              </PhoneMockup>
            </motion.div>
            
            {/* Right, copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center lg:text-left order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
                <Flame className="w-3.5 h-3.5 fill-current" />
                The streak effect
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4">
                30 days later,
                <span className="block bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">
                  you'll Really feel it.
                </span>
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-6 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                One small check-in a day compounds fast. By day 30, most couples say they argue less, listen more, and feel closer, without trying harder. Just showing up.
              </p>
              
              {/* Mini stat row */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-lg mx-auto lg:mx-0">
                <div className="flex-1 bg-white rounded-2xl p-4 shadow-sm border border-pink-100">
                  <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">73%</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">of users hit a 30-day streak in their first 3 months</p>
                </div>
                <div className="flex-1 bg-white rounded-2xl p-4 shadow-sm border border-pink-100">
                  <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">2 min</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">average time spent per day. That's it</p>
                </div>
              </div>
              
              <p className="text-xs text-gray-400 mt-4 italic">
                Streaks don't measure perfection. They measure presence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section id="testimonials" className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-pink-50 to-orange-50 scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Loved by Couples</h2>
            <p className="text-base sm:text-lg text-gray-600">Real stories. Real couples. Real life.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center text-2xl">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.years}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section id="how-it-works" className="py-14 sm:py-20 lg:py-28 bg-white relative overflow-hidden scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-pink-50 text-pink-600 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" />
              <span>Effortless 3-Step Routine</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              How CoupleIn Works for You
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              No complicated relationship homework. Just simple micro-habits designed to make love feel lighter every single day.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 relative">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-b from-pink-50/60 to-white rounded-3xl p-7 border border-pink-100/80 shadow-sm relative group hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-pink-500 text-white rounded-2xl flex items-center justify-center font-bold text-xl mb-6 shadow-pink">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Connect in 60 Seconds</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Download free on iOS or Android. Share your personal 6-digit invite code or link with your partner to sync your accounts instantly.
              </p>
              <div className="bg-white rounded-xl p-3 border border-pink-100/60 flex items-center gap-2 text-xs text-pink-600 font-semibold">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span>Works even if your partner joins later</span>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="bg-gradient-to-b from-orange-50/60 to-white rounded-3xl p-7 border border-orange-100/80 shadow-sm relative group hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl flex items-center justify-center font-bold text-xl mb-6 shadow-sm">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Daily 60-Second Rituals</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Do a quick morning mood check-in, award Brownie Points when they do something sweet, and drop upcoming plans into your shared calendar.
              </p>
              <div className="bg-white rounded-xl p-3 border border-orange-100/60 flex items-center gap-2 text-xs text-orange-600 font-semibold">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>Takes less than 2 minutes a day</span>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-gradient-to-b from-purple-50/60 to-white rounded-3xl p-7 border border-purple-100/80 shadow-sm relative group hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-purple-500 text-white rounded-2xl flex items-center justify-center font-bold text-xl mb-6 shadow-sm">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Strengthen Your Bond</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Build your connection streak, solve misunderstandings with fair 5-minute Guided Mode, and enjoy feeling appreciated every day.
              </p>
              <div className="bg-white rounded-xl p-3 border border-purple-100/60 flex items-center gap-2 text-xs text-purple-600 font-semibold">
                <Heart className="w-4 h-4 flex-shrink-0 fill-current" />
                <span>Noticeable closeness in 7 days</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Section: Why CoupleIn */}
      <section id="comparison" className="py-14 sm:py-20 lg:py-28 bg-gray-50/80 border-y border-gray-100 scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Why Plain Notes & Messaging Don't Work
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Your relationship deserves a dedicated space built specifically for two.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* The Old Way */}
            <div className="bg-white rounded-3xl p-7 sm:p-9 border border-gray-200 shadow-sm">
              <div className="inline-block bg-gray-100 text-gray-700 font-bold px-3.5 py-1.5 rounded-full text-xs mb-6 uppercase tracking-wider">
                The Old Scattered Way
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-600">
                  <div className="w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-xs">✕</div>
                  <span>Important anniversaries and date nights get lost in messaging group threads.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-600">
                  <div className="w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-xs">✕</div>
                  <span>Daily favors and sweet gestures go unacknowledged and forgotten.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-600">
                  <div className="w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-xs">✕</div>
                  <span>Disagreements spiral over angry texts or silent treatments without fair turns.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-600">
                  <div className="w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-xs">✕</div>
                  <span>No visibility into each other's mood before coming home after a stressful day.</span>
                </li>
              </ul>
            </div>

            {/* The CoupleIn Way */}
            <div className="bg-gradient-to-br from-pink-500 to-rose-600 text-white rounded-3xl p-7 sm:p-9 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 -mr-8 -mt-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              <div className="inline-block bg-white/20 text-white font-bold px-3.5 py-1.5 rounded-full text-xs mb-6 uppercase tracking-wider backdrop-blur-sm">
                With CoupleIn
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm sm:text-base text-white/95">
                  <CheckCircle2 className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                  <span>One shared source of truth for all events, countdowns, and anniversaries.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-white/95">
                  <CheckCircle2 className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                  <span>Fun Brownie Points gratitude engine makes doing sweet gestures rewarding.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-white/95">
                  <CheckCircle2 className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                  <span>5-Minute Guided Mode ensures fair turns, zero interruptions, and quick resolutions.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-white/95">
                  <CheckCircle2 className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                  <span>60-second daily mood sync keeps you emotionally tuned-in without awkward asking.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Relationship Quizzes Teaser Section */}
      <section id="quizzes" className="py-14 sm:py-20 lg:py-28 bg-white scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-50 text-purple-600 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4">
              <Heart className="w-4 h-4 fill-current" />
              <span>100% Free · No Sign-up Required</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Discover Your Relationship Dynamics
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Take our interactive science-backed quizzes to understand how you and your partner give love and handle emotions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {/* Love Language Quiz */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-7 sm:p-8 border border-pink-100 shadow-sm flex flex-col justify-between group hover:shadow-md transition-shadow">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-pink-500 text-white flex items-center justify-center mb-5 shadow-pink">
                  <Heart className="w-6 h-6 fill-current" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Love Language Quiz</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Do you feel most loved through Words of Affirmation, Quality Time, Gifts, Acts of Service, or Physical Touch? Find out in 2 minutes.
                </p>
              </div>
              <Link
                to="/quiz/love-language"
                data-testid="landing-love-quiz-cta"
                className="inline-flex items-center justify-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-semibold py-3.5 px-6 rounded-2xl transition-all hover:scale-105 active:scale-95 text-sm"
              >
                <span>Take Love Language Quiz</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Attachment Style Quiz */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-7 sm:p-8 border border-purple-100 shadow-sm flex flex-col justify-between group hover:shadow-md transition-shadow">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-purple-600 text-white flex items-center justify-center mb-5 shadow-sm">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Attachment Style Quiz</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Understand whether your bond style is Secure, Anxious, Avoidant, or Fearful, and discover how to navigate communication during conflict.
                </p>
              </div>
              <Link
                to="/quiz/attachment-style"
                data-testid="landing-attachment-quiz-cta"
                className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3.5 px-6 rounded-2xl transition-all hover:scale-105 active:scale-95 text-sm"
              >
                <span>Take Attachment Quiz</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions (FAQ) */}
      <section id="faq" className="py-14 sm:py-20 lg:py-28 bg-gray-50/80 border-t border-gray-100 scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-pink-50 text-pink-600 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4">
              <HelpCircle className="w-4 h-4" />
              <span>Clear Answers</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about CoupleIn and getting started.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "Can I use CoupleIn if my partner hasn't downloaded it yet?",
                a: "Yes, absolutely! You can start using CoupleIn right away to keep track of shared reminders, take quizzes, and log sweet gestures. When your partner is ready, simply share your 6-digit sync code or link and you will be paired in one tap."
              },
              {
                q: "Is CoupleIn free on iOS and Android?",
                a: "Yes! CoupleIn is free on the Apple App Store and Google Play Store. You get instant access to Brownie Points, Shared Calendar, 60-Second Mood Sync, and Guided Conflict Resolution without requiring a credit card."
              },
              {
                q: "How does the Brownie Points system work?",
                a: "You pick or create gestures that mean the most to you (like making coffee, giving a foot rub, or cooking dinner). Whenever your partner does it, you tap to give them points, which they can redeem for fun, customized rewards you both agreed on."
              },
              {
                q: "How does 5-Minute Guided Mode resolve disagreements?",
                a: "Instead of texting in anger or talking over each other, Guided Mode provides structured, timed turns where each partner shares how they feel without interruption, followed by actionable resolution prompts to help you reconnect."
              },
              {
                q: "Is our data private and secure?",
                a: "Yes. Your shared calendar, mood check-ins, gestures, and conversations are encrypted and accessible only by you and your paired partner. We never sell your personal relationship data."
              }
            ].map((faq, idx) => (
              <details
                key={idx}
                className="group bg-white rounded-2xl p-5 sm:p-6 border border-gray-200/80 shadow-xs open:shadow-md transition-all cursor-pointer"
                data-testid={`faq-item-${idx}`}
              >
                <summary className="font-bold text-base sm:text-lg text-gray-900 flex items-center justify-between gap-4 list-none select-none">
                  <span>{faq.q}</span>
                  <span className="w-8 h-8 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center flex-shrink-0 group-open:rotate-180 transition-transform duration-200">
                    <ChevronDown className="w-5 h-5" />
                  </span>
                </summary>
                <p className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed pt-2 border-t border-gray-100">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Blog Posts Section (renders when posts are published) */}
      {latestPosts.length > 0 && (
        <section id="blog-section" className="py-14 sm:py-20 lg:py-28 bg-white border-t border-gray-100 scroll-mt-24 sm:scroll-mt-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-10 sm:mb-14 gap-4 text-center sm:text-left">
              <div>
                <div className="inline-flex items-center gap-2 bg-pink-50 text-pink-600 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-3">
                  <Heart className="w-4 h-4 fill-current" />
                  <span>Relationship Advice & Guides</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
                  Latest from Our Blog
                </h2>
              </div>
              <Link
                to="/blog"
                data-testid="landing-view-all-blogs-btn"
                className="inline-flex items-center gap-2 text-pink-600 hover:text-pink-700 font-semibold text-sm group bg-pink-50/80 hover:bg-pink-100/80 px-4 py-2.5 rounded-2xl transition-all"
              >
                <span>View All Articles</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              {latestPosts.map((post) => (
                <motion.article
                  key={post.id || post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl overflow-hidden border border-gray-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group"
                >
                  <Link to={`/${post.slug}`} className="block">
                    <div className="aspect-video bg-gradient-to-br from-pink-100 to-orange-100 overflow-hidden relative">
                      {post.image ? (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl">
                          {post.emoji || '💕'}
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      {post.category && (
                        <span className="inline-block bg-pink-50 text-pink-600 text-xs font-semibold px-3 py-1 rounded-full mb-3 border border-pink-100">
                          {post.category}
                        </span>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4 leading-relaxed">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1 font-semibold text-pink-500 group-hover:translate-x-0.5 transition-transform">
                          Read Article →
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24 pb-24 sm:pb-16 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-pink-500 to-orange-400 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 text-white"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">Ready to make the everyday a little better?</h2>
            <p className="text-base sm:text-lg opacity-95 mb-2 max-w-xl mx-auto">
              Join thousands of couples making the small stuff count. Download free today.
            </p>
            <p className="text-xs sm:text-sm opacity-80 mb-6 sm:mb-8 px-2">
              Free · 60-second setup · Works even if your partner hasn't joined yet.
            </p>
            <DownloadButtons size="large" className="justify-center" />
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src={LOGO_URL}
                alt="CoupleIn Logo"
                width="191"
                height="64"
                className="h-14 sm:h-16 mb-4 brightness-0 invert w-auto object-contain"
              />
              <p className="text-gray-400 text-sm">For the great days, the off days, and everything in between.</p>
            </div>
            <div>
              <h3 className="text-base font-semibold mb-4 text-white">Product</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#screenshots" className="hover:text-white transition-colors">Screenshots</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Reviews</a></li>
                <li><Link to="/never-forget-anything" data-testid="footer-never-forget-link" className="hover:text-white transition-colors">Never Forget Anything</Link></li>
                <li><Link to="/solve-fights-in-5-minutes" data-testid="footer-solve-fights-link" className="hover:text-white transition-colors">Solve Fights in 5 Minutes</Link></li>
                <li><Link to="/love-with-intentionality" data-testid="footer-brownie-link" className="hover:text-white transition-colors">Love with Intentionality</Link></li>
                <li><Link to="/stay-connected-in-60-seconds" data-testid="footer-moodsync-link" className="hover:text-white transition-colors">Stay Connected Daily</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-base font-semibold mb-4 text-white">Resources</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-base font-semibold mb-4 text-white">Download</h3>
              <DownloadButtons size="small" className="flex-col" />
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>© {new Date().getFullYear()} Couple In LTD · Company No. 16342045 · Registered in England &amp; Wales. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
